import React, { useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import styles from "./FeedMediaSlider.module.css";
import { getFeedImageUrl, preloadImages } from "../utils/imageUtils";

interface MediaFile {
  url: string;
  type: string; // "image" 또는 "video"
  name?: string;
}

interface FeedMediaSliderProps {
  mediaFiles: MediaFile[];
}

const FeedMediaSlider: React.FC<FeedMediaSliderProps> = ({ mediaFiles }) => {
  // 비디오 재생 상태만 간단히 관리
  const [playingVideos, setPlayingVideos] = useState<Set<number>>(new Set());
  const [mutedVideos, setMutedVideos] = useState<Set<number>>(new Set());
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});

  // 초기 음소거 상태 설정 (모든 비디오는 muted로 시작)
  useEffect(() => {
    const videoIndexes = mediaFiles
      .map((media, index) =>
        media.type.startsWith("video/") || media.type === "video" ? index : null
      )
      .filter((index): index is number => index !== null);

    if (videoIndexes.length > 0) {
      setMutedVideos(new Set(videoIndexes));
    }
  }, [mediaFiles]);

  // 이미지 preload 및 최적화
  useEffect(() => {
    const imageUrls = mediaFiles
      .filter((media, index) => isImage(media.type))
      .map((media) => getFeedImageUrl(media.url, "feed"));

    if (imageUrls.length > 0) {
      preloadImages(imageUrls).catch(console.warn);
    }
  }, [mediaFiles]);

  if (!mediaFiles.length) return null;

  // 파일 타입 확인 함수
  const isVideo = (type: string) =>
    type.startsWith("video/") || type === "video";
  const isImage = (type: string) =>
    type.startsWith("image/") || type === "image";

  // 비디오 재생/일시정지 토글 (단순화)
  const toggleVideoPlay = async (index: number) => {
    const video = videoRefs.current[index];
    if (!video) return;

    try {
      if (video.paused) {
        await video.play();
        setPlayingVideos((prev) => new Set(prev).add(index));
      } else {
        video.pause();
        setPlayingVideos((prev) => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
      }
    } catch (error) {
      console.log(`Video play failed silently (index ${index})`);
    }
  };

  // 음소거 토글 (단순화)
  const toggleMute = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.muted = !video.muted;
      setMutedVideos((prev) => {
        const newSet = new Set(prev);
        if (video.muted) {
          newSet.add(index);
        } else {
          newSet.delete(index);
        }
        return newSet;
      });
    }
  };

  return (
    <div className={styles.sliderContainer}>
      <Swiper
        modules={[Navigation, Pagination]}
        navigation={
          mediaFiles.length > 1
            ? {
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
              }
            : false
        }
        pagination={
          mediaFiles.length > 1
            ? {
                clickable: true,
                type: "bullets",
              }
            : false
        }
        className={styles.swiper}
        spaceBetween={0}
      >
        {mediaFiles.map((media, index) => (
          <SwiperSlide key={index} className={styles.slide}>
            {isVideo(media.type) ? (
              <div className={styles.videoContainer}>
                <video
                  ref={(el) => {
                    videoRefs.current[index] = el;
                  }}
                  src={media.url}
                  className={styles.video}
                  preload="metadata"
                  playsInline
                  muted
                  loop
                  controls={false}
                  onClick={() => toggleVideoPlay(index)}
                >
                  <source src={media.url} type={media.type} />
                  동영상을 재생할 수 없습니다.
                </video>

                {/* 재생 버튼 - 일시정지 상태일 때만 표시 */}
                {!playingVideos.has(index) && (
                  <div
                    className={styles.playButton}
                    onClick={() => toggleVideoPlay(index)}
                  >
                    <div className={styles.playIcon}>▶</div>
                  </div>
                )}

                {/* 음소거 버튼 */}
                <div className={styles.videoControls}>
                  <button
                    className={styles.muteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMute(index);
                    }}
                  >
                    <span className={styles.muteIcon}>
                      {mutedVideos.has(index) ? (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M16.5 12L22 6.5L20.5 5L15 10.5L9.5 5L8 6.5L13.5 12L8 17.5L9.5 19L15 13.5L20.5 19L22 17.5L16.5 12Z"
                            fill="white"
                          />
                          <path d="M3 9V15H7L12 20V4L7 9H3Z" fill="white" />
                        </svg>
                      ) : (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path d="M3 9V15H7L12 20V4L7 9H3Z" fill="white" />
                          <path
                            d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V16.02C15.48 15.29 16.5 13.77 16.5 12Z"
                            fill="white"
                          />
                          <path
                            d="M14 3.23V5.29C16.89 6.15 19 8.83 19 12C19 15.17 16.89 17.85 14 18.71V20.77C18.01 19.86 21 16.28 21 12C21 7.72 18.01 4.14 14 3.23Z"
                            fill="white"
                          />
                        </svg>
                      )}
                    </span>
                  </button>
                </div>
              </div>
            ) : isImage(media.type) ? (
              <div className={styles.imageContainer}>
                {/* 로딩 스피너 */}
                {!loadedImages.has(index) && !imageErrors.has(index) && (
                  <div className={styles.imageLoader}>
                    <div className={styles.spinner}></div>
                  </div>
                )}

                <img
                  src={getFeedImageUrl(media.url, "feed")}
                  alt={media.name || `미디어 ${index + 1}`}
                  className={`${styles.image} ${
                    loadedImages.has(index) ? styles.loaded : ""
                  }`}
                  loading="lazy"
                  decoding="async"
                  onLoad={() => {
                    setLoadedImages((prev) => new Set(prev).add(index));
                  }}
                  onError={() => {
                    setImageErrors((prev) => new Set(prev).add(index));
                  }}
                />

                {/* 에러 상태 */}
                {imageErrors.has(index) && (
                  <div className={styles.imageError}>
                    <span>이미지를 불러올 수 없습니다</span>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.unsupportedMedia}>
                <p>지원되지 않는 미디어 형식입니다.</p>
              </div>
            )}
          </SwiperSlide>
        ))}
        {mediaFiles.length > 1 && (
          <>
            <div className="swiper-button-prev"></div>
            <div className="swiper-button-next"></div>
          </>
        )}
      </Swiper>
    </div>
  );
};

export default FeedMediaSlider;
